import { Link, useLocation } from "wouter";
import {
  LayoutDashboard, Users, Calculator, BarChart3,
  ArrowLeft, DoorOpen, GraduationCap, BookOpen, Briefcase, LineChart, UserCircle, Tag,
  Award, Gift, FileText, Sparkles, MessageSquare
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useProfile, PersonaId } from "@/lib/profile";
import { ProfileSwitcher } from "@/components/profile-switcher";

type NavSection = {
  label?: string;
  items: { name: string; href: string; icon: any }[];
};

const NAV_BY_PERSONA: Record<PersonaId, NavSection[]> = {
  consultant: [
    {
      items: [
        { name: "Dashboard",      href: "/dashboard",   icon: LayoutDashboard },
        { name: "Transitions",    href: "/transitions", icon: Users },
        { name: "ROI Calculator", href: "/calculator",  icon: Calculator },
        { name: "Reports",        href: "/reports",     icon: BarChart3 },
      ],
    },
    {
      label: "Sales",
      items: [
        { name: "Pricing",        href: "/pricing",             icon: Tag },
        { name: "Advanced ROI",   href: "/calculator/advanced", icon: Calculator },
      ],
    },
    {
      label: "Workflows",
      items: [
        { name: "Exit Workflow",  href: "/exit-workflow",       icon: DoorOpen },
        { name: "Onboarding",     href: "/onboarding-workflow", icon: GraduationCap },
      ],
    },
  ],
  contractor: [
    {
      items: [
        { name: "My Cases",       href: "/transitions", icon: Briefcase },
        { name: "Dashboard",      href: "/dashboard",   icon: LayoutDashboard },
        { name: "Reports",        href: "/reports",     icon: BarChart3 },
      ],
    },
    {
      label: "Workflows",
      items: [
        { name: "Exit Workflow",  href: "/exit-workflow",       icon: DoorOpen },
        { name: "Onboarding",     href: "/onboarding-workflow", icon: GraduationCap },
      ],
    },
  ],
  departing: [
    {
      items: [
        { name: "My Handover",       href: "/me",               icon: UserCircle },
        { name: "Knowledge Book",    href: "/me/book",          icon: BookOpen },
      ],
    },
    {
      label: "What you're building",
      items: [
        { name: "Expertise Signal",  href: "/me/profile",       icon: Award },
        { name: "Deliverables",      href: "/me/deliverables",  icon: FileText },
        { name: "Legacy Report",     href: "/me/legacy",        icon: Gift },
      ],
    },
  ],
  onboarding: [
    {
      items: [
        { name: "My Onboarding",     href: "/me",               icon: GraduationCap },
        { name: "Ask Predecessor",   href: "/me/ask",           icon: Sparkles },
        { name: "Knowledge Book",    href: "/me/book",          icon: BookOpen },
      ],
    },
  ],
  ceo: [
    {
      items: [
        { name: "Executive View",    href: "/me",        icon: LineChart },
        { name: "Reports",           href: "/reports",   icon: BarChart3 },
      ],
    },
  ],
};

function NavItem({ item, location }: { item: { name: string; href: string; icon: any }; location: string }) {
  const isActive =
    location === item.href ||
    (item.href !== "/dashboard" && item.href !== "/me" && location.startsWith(item.href)) ||
    (item.href === "/me" && location === "/me");

  return (
    <Link
      key={item.name}
      href={item.href}
      className={cn(
        isActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
          : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
        "group flex items-center px-3 py-2.5 text-sm rounded-md transition-colors"
      )}
    >
      <item.icon
        className={cn(
          isActive ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/50 group-hover:text-sidebar-foreground/70",
          "flex-shrink-0 mr-3 h-5 w-5"
        )}
        aria-hidden="true"
      />
      {item.name}
    </Link>
  );
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { persona } = useProfile();
  const sections = NAV_BY_PERSONA[persona.id];

  return (
    <div className="flex h-screen bg-muted/30">
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-sidebar border-r border-sidebar-border">
        <div className="flex flex-col flex-grow pt-5 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4 mb-7">
            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center mr-3">
              <span className="text-primary-foreground font-bold text-lg leading-none">R</span>
            </div>
            <span className="text-sidebar-foreground font-semibold text-xl tracking-tight">Relay</span>
          </div>

          <div className="mt-2 flex-1 flex flex-col px-3 space-y-6">
            {sections.map((section, i) => (
              <div key={i}>
                {section.label && (
                  <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-sidebar-foreground/40">
                    {section.label}
                  </p>
                )}
                <nav className="space-y-1">
                  {section.items.map((item) => (
                    <NavItem key={item.name} item={item} location={location} />
                  ))}
                </nav>
              </div>
            ))}
          </div>

          <div className="flex-shrink-0 p-3 border-t border-sidebar-border/50">
            <ProfileSwitcher />
            <Link
              href="/"
              className="mt-2 flex w-full items-center px-3 py-2 text-sm font-medium text-sidebar-foreground/60 rounded-md hover:bg-sidebar-accent/40 hover:text-sidebar-foreground transition-colors"
            >
              <ArrowLeft className="mr-3 h-4 w-4" />
              Back to site
            </Link>
          </div>
        </div>
      </div>

      <div className="md:pl-64 flex flex-col flex-1 w-full">
        <main className="flex-1">
          <div className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
